import { spineLoader, SpineView } from '@play-co/timestep-spine';
import { playSpineAnimationSequence } from 'src/lib/ui/utils/spine';
import { clamp, Container } from '../../../odie';
import { getMatch2AssetPath } from '../../util/assets';
import { blockPositionView } from '../../util/blockTools';
import { MagicBunniesBlockProps } from './MagicBunniesBlock';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import { CountBlockOptionDef } from '../../defs/block';
import sounds from 'src/lib/sounds';

export const magicBunniesAssets = {
  base: getMatch2AssetPath('blocks/magicBunnies/base.png'),
  piece01: getMatch2AssetPath('blocks/magicBunnies/piece01.png'),
  piece02: getMatch2AssetPath('blocks/magicBunnies/piece02.png'),
  piece03: getMatch2AssetPath('blocks/magicBunnies/piece03.png'),
  piece04: getMatch2AssetPath('blocks/magicBunnies/piece04.png'),
  spine: {
    loadMethod: spineLoader,
    url: getMatch2AssetPath(
      'blocks/magicBunnies/animation/spine.magichatbunny.obstacle',
    ),
  },
};

export class MagicBunniesBlockView extends Container {
  public box: ImageScaleView;
  public bunnies: SpineView[] = [];
  private _count = 0;

  constructor(props: MagicBunniesBlockProps, count: number) {
    super();
    blockPositionView(this, props);
    this.box = new ImageScaleView({
      superview: this.view,
      image: magicBunniesAssets.base,
      scaleMethod: 'contain',
      width: 152,
      height: 152,
      x: -76,
      y: -76,
      canHandleEvents: false,
    });
    this._count = count;
    this.createBunnies();
  }

  private async createBunnies() {
    // Do not create again if already created
    if (this.bunnies.length) return;

    // Ensure spine assets are loaded
    await spineLoader(magicBunniesAssets.spine.url);

    const spacingX = 64;
    const spacingY = 60;
    const baseX = -spacingX / 2;
    const baseY = 0;
    for (let i = 0; i < 4; i++) {
      const bunny = new SpineView({
        superview: this.view,
        spinePath: magicBunniesAssets.spine.url,
        x: baseX + (i % 2) * spacingX,
        y: baseY + Math.floor(i / 2) * spacingY,
        scale: DOWNSCALE_FACTOR,
      });
      // Add bunnies in reverse order soe they weill be cleared from top-left (last bunny added)
      this.bunnies.unshift(bunny);
    }

    this.updateBunnies({ silent: true });
  }

  public static assets() {
    return Object.values(magicBunniesAssets);
  }

  public get count(): number {
    return this._count;
  }

  public set count(value: number) {
    this._count = clamp(value, 0, 4);
    this.updateBunnies();
  }

  public updateBunnies(args?: { silent: boolean }) {
    this.bunnies.forEach((bunny, index) => {
      if (index < this._count) {
        this.updateBunny({ index, state: 'showing', silent: args?.silent });
      } else {
        this.updateBunny({ index, state: 'hidden', silent: args?.silent });
      }
    });
  }

  private async updateBunny(args: {
    index: number;
    state: 'showing' | 'hidden';
    silent?: boolean;
  }) {
    const { index, state, silent } = args;
    const bunny = this.bunnies[index];
    if (bunny.tag === state) return;
    bunny.tag = state;
    switch (state) {
      case 'showing':
        if (!silent) sounds.playSound('puzzle-bubble-form');
        playSpineAnimationSequence(bunny, {
          sequence: ['bunny_spawn', 'bunny_idle'],
          loopLast: true,
          clear: true,
        });
        break;
      case 'hidden':
        if (!silent) sounds.playSound('puzzle-lock-destroy');
        playSpineAnimationSequence(bunny, {
          sequence: ['bunny_break', 'hat_idle'],
          loopLast: true,
          clear: true,
        });
        break;
    }
  }
}

export function _createMagicBunniesBlockView(
  props: MagicBunniesBlockProps,
  option: number,
): MagicBunniesBlockView {
  const optionDef = props.options![option] as CountBlockOptionDef;
  return new MagicBunniesBlockView(props, optionDef.count);
}
